/**
 * ProDowngrade: the Pro-only data a case holds, as the server reports it
 * (`POST /v1/cases/{caseId}/pro-data-reasons`, built by ProDowngradeConverter.ProDataReasons -- the
 * same walker as the read-only lock, so the list cannot drift from the lock).
 *
 * Codes mirror `ProDataReasonCodes` in lib/src/Services/ProDowngradeConverter.cs.
 */

export interface ProDataReason {
    code?: string | null;
    description?: string | null;
}

export const PRO_DATA_REASON_CODES = {
    divisionExclusions: 'divisionExclusions',
    splitCustody: 'splitCustody',
    printedNotes: 'printedNotes',
} as const;

/** Wording when the server sent a code with no description. */
const FALLBACK_LABELS: Record<string, string> = {
    divisionExclusions: 'Division exclusions.',
    splitCustody: 'Split custody.',
    printedNotes: 'Printed notes.',
};

/**
 * Generic wording used when the reasons could not be read. Lists every kind of Pro data the lock
 * can count, so the user is never told less than the copy may remove.
 */
export const GENERIC_PRO_DATA_SUMMARY =
    'division exclusions, split custody, and printed notes on income, taxes, deductions, medical or step downs';

/**
 * The lines to show, one per kind, in the order the server sent them. Drops blanks and
 * duplicates; uses a fallback label for a code with no description.
 */
export function formatProDataReasons(reasons: ProDataReason[] | null | undefined): string[] {
    if (!reasons) return [];
    const seenCodes = new Set<string>();
    const lines: string[] = [];
    for (const reason of reasons) {
        const code = reason?.code?.trim() ?? '';
        if (code !== '' && seenCodes.has(code)) continue;
        const text = reason?.description?.trim() || (code !== '' ? FALLBACK_LABELS[code] : undefined);
        if (!text) continue;
        if (code !== '') seenCodes.add(code);
        if (!lines.includes(text)) lines.push(text);
    }
    return lines;
}

/**
 * Removing exclusions can leave assets not fully allocated on the copy, so the user is told to
 * review the Division step -- only when exclusions are actually among the reasons, or when the
 * reasons are unknown.
 */
export function shouldWarnAboutDivision(reasons: ProDataReason[] | null | undefined): boolean {
    if (!reasons) return true;
    return reasons.some((r) => r?.code === PRO_DATA_REASON_CODES.divisionExclusions);
}

/** True when the copy drops split custody, so the user must choose the arrangement to use instead. */
export function needsCustodyChoice(reasons: ProDataReason[] | null | undefined): boolean {
    return !!reasons?.some((r) => r?.code === PRO_DATA_REASON_CODES.splitCustody);
}

export type NonSplitCustodyArrangement = 'shared' | 'partyA' | 'partyB';

/**
 * The wizard's non-split custody choices (CustodyPage), in its order and with its labels. The
 * server accepts exactly these values (ProDowngradeConverter.NonSplitCustodyArrangements).
 */
export function nonSplitCustodyOptions(partyNames?: { partyA?: string | null; partyB?: string | null }):
    { value: NonSplitCustodyArrangement; label: string }[] {
    const a = partyNames?.partyA?.trim() || 'Party A';
    const b = partyNames?.partyB?.trim() || 'Party B';
    return [
        { value: 'shared', label: 'Shared' },
        { value: 'partyA', label: `${a} has Primary` },
        { value: 'partyB', label: `${b} has Primary` },
    ];
}
